import h from 'hyperscript';

import type { CellRendererContext, Column } from '@disy/ui.responsive-table/responsive-table';
import { ACTIONS_COLUMN_KEY } from '@disy/ui.responsive-table/responsive-table-constants';
import { attachGroupData, attachItemData } from '@disy/ui.responsive-table/responsive-table-utils';

export interface RowRendererContext<T> {
  /** The columns definitions */
  columns: Column<T>[];
  /** Row index, is rowOffset plus the local row index */
  rowIndex: number;
  /** The item data of the complete row */
  rowData: T;
  /** The group data of the data item if grouping is applied */
  groupData: unknown;
  /** How many rows are there in total. */
  rowCount: number;
  /** The table element in which the row is rendered */
  table: HTMLTableElement;
  /** ID, used to identify the heading of the row in labels */
  rowHeadingId: string;
}

/**
 * Interface for row renderer functions, that create a TR element for a given row.
 *
 * There is a default row renderer that is used in most cases, but users are free to define their
 * own renderers that can render rows differently based on each row's data.
 *
 * Custom renderers can create everything from scratch or re-use the default renderer and perform
 * additional actions like special styling.
 *
 * @param context - An object with context information for the renderer
 * @return A TR element that will be inserted into the TBODY element of the table.
 */
export type RowRenderer<T> = (context: RowRendererContext<T>) => HTMLTableRowElement;

/* Default row renderer that is always used if no default renderer is specified. */
export function defaultRowRenderer<T>(options: RowRendererContext<T>): HTMLTableRowElement {
  const { rowData, groupData, columns } = options;
  const tr = h<HTMLTableRowElement>('tr');
  attachItemData(tr, rowData);
  attachGroupData(tr, groupData);
  tr.append(...columns.map((column) => createCell(column, { ...options, tr })));
  return tr;
}

function createCell<T>(column: Column<T>, context: CellRendererContext<T>) {
  const { main, key, renderer = (cellData) => String(cellData ?? ''), align = 'left', ellipsis = true } = column;
  const cell = h<HTMLTableCellElement>(main ? 'th' : 'td', renderer(context.rowData[key], context));
  // .ellipsis on Action Column breaks the focus outline of action buttons
  cell.classList.toggle('ellipsis', ellipsis && key !== ACTIONS_COLUMN_KEY);
  if (align !== 'left') {
    cell.classList.add(`align-${align}`);
  }
  return cell;
}
